import { useEffect, useRef, useState } from 'react';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { fetchShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Button, Card } from '@shared/components/ui';
import { StatusChip } from '@shared/components/StatusChip';
import { getStatusTone } from '@shared/config/theme';
import type { User } from '@shared/layouts/MainLayout';

interface Export {
  id: string;
  status: string;
  format?: 'json' | 'csv';
  expires_at: string;
  completed_at?: string;
  created_at: string;
  download_url?: string;
}

interface ExportStatus {
  export_id: string;
  status: string;
  message: string;
  expires_at?: string;
  download_url?: string;
}

export default function DataExport() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('compliance');
  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userLoadError, setUserLoadError] = useState(false);
  const [exports, setExports] = useState<Export[]>([]);
  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const exportsRef = useRef<Export[]>([]);
  const exportsFetchStarted = useRef(false);
  const [loading, setLoading] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'json' | 'csv'>('json');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pollingExportId, setPollingExportId] = useState<string | null>(null);
  const [statusError, setStatusError] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    setUserLoading(true);
    setUserLoadError(false);
    try {
      // Shared with useUser and the other compliance pages — one request per
      // page load instead of one per component.
      const data = await fetchShared<{ user: User }>(SHARED_KEYS.profile, async () => {
        const response = await api.get<{ user: User }>('/auth/profile');
        return response.data;
      });
      setUser(data.user);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      console.error('Failed to fetch user:', err);
      setUserLoadError(true);
    } finally {
      setUserLoading(false);
    }
  };

  // Keep the poll's ref pointing at the latest value.
  exportsRef.current = exports;
  useEffect(() => {
    if (exportsFetchStarted.current) return;
    exportsFetchStarted.current = true;
    fetchExports();
  }, []);

  // Watch a pending export. Adaptive: 5s base backing off to 30s once the
  // status stops changing, and paused while the tab is hidden.
  // `checkExportStatus` clears `pollingExportId` when the export settles, which
  // disables this poll on the next render.
  useAdaptivePoll({
    enabled: !!pollingExportId,
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      const id = pollingExportId;
      if (!id) return '';
      await checkExportStatus(id);
      const current = exportsRef.current.find((e) => e.id === id);
      return `${current?.status ?? ''}:${current?.download_url ?? ''}`;
    },
    isEqual: (a, b) => a === b,
  });

  const fetchExports = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ exports: Export[]; count: number }>('/compliance/exports');
      setExports(response.data.exports || []);
      setHistoryError(false);
    } catch (err: any) {
      if (err.response?.status === 401) {
        return;
      }
      console.error('Failed to fetch exports:', err);
      setHistoryError(true);
    } finally {
      setLoading(false);
    }
  };

  const checkExportStatus = async (exportId: string) => {
    try {
      const response = await api.get<ExportStatus>(`/compliance/export?id=${exportId}`);
      const exportStatus = response.data;
      setStatusError(false);

      // Update exports list with latest status
      setExports((prevExports) =>
        (prevExports || []).map((exp) =>
          exp.id === exportId
            ? {
                ...exp,
                status: exportStatus.status,
                download_url: exportStatus.download_url,
                completed_at:
                  exportStatus.status === 'COMPLETED' ? new Date().toISOString() : exp.completed_at,
              }
            : exp
        )
      );

      // Stop polling if export is completed or failed
      if (exportStatus.status === 'COMPLETED' || exportStatus.status === 'FAILED') {
        setPollingExportId(null);
        if (exportStatus.status === 'COMPLETED') {
          setMessage({ type: 'success', text: t('dataExport.messages.ready') });
          setTimeout(() => setMessage(null), 5000);
        } else {
          setMessage({ type: 'error', text: t('dataExport.errors.processingFailed') });
        }
      }
    } catch {
      setStatusError(true);
    }
  };

  const handleRequestExport = async () => {
    if (requesting) return;
    setMessage(null);
    setRequesting(true);

    try {
      const response = await api.post<{
        export_id: string;
        status: string;
        message: string;
        expires_at: string;
      }>('/compliance/export-data', {
        format: selectedFormat,
      });

      const newExport = response.data;

      setMessage({
        type: 'success',
        text: t('dataExport.messages.requested'),
      });

      // Start polling for this export
      setPollingExportId(newExport.export_id);

      // Add to exports list
      setExports((prevExports) => [
        {
          id: newExport.export_id,
          status: newExport.status,
          format: selectedFormat,
          expires_at: newExport.expires_at,
          created_at: new Date().toISOString(),
        },
        ...(prevExports || []),
      ]);

      setTimeout(() => setMessage(null), 5000);
    } catch {
      setMessage({
        type: 'error',
        text: t('dataExport.errors.requestFailed'),
      });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setRequesting(false);
    }
  };

  const handleDownload = (exportItem: Export) => {
    if (downloadingId) return;
    if (exportItem.download_url) {
      setMessage(null);
      const token = localStorage.getItem('auth_token');
      const parsedUrl = new URL(exportItem.download_url, window.location.origin);
      parsedUrl.searchParams.set('id', exportItem.id);
      const downloadUrl = parsedUrl.toString();
      setDownloadingId(exportItem.id);

      // Open in new tab with auth token
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute(
        'download',
        `export_${exportItem.id}.${exportItem.format || selectedFormat}`
      );
      document.body.appendChild(link);

      // Add auth header to request
      fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Download failed');
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.click();
          window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
          document.body.removeChild(link);
        })
        .catch((err) => {
          console.error('Download failed:', err);
          setMessage({ type: 'error', text: t('dataExport.errors.downloadFailed') });
          setTimeout(() => setMessage(null), 5000);
          document.body.removeChild(link);
        })
        .finally(() => setDownloadingId(null));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const getStatusLabel = (status: string) =>
    t(`dataExport.status.${status.toLowerCase()}`, { defaultValue: status });

  const pageHeader = (
    <header>
      <h1 className="text-2xl font-bold leading-7 text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
        {t('dataExport.title')}
      </h1>
      <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
        {t('dataExport.subtitle')}
      </p>
    </header>
  );

  if (!user && userLoading) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {pageHeader}
        <div className="flex min-h-64 items-center justify-center" role="status" aria-live="polite">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-brand-600" />
            <p className="mt-4 text-sm text-text-muted-light dark:text-text-muted-dark">
              {t('dataExport.loading')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {pageHeader}
        <Card className="space-y-4 p-4 sm:p-6">
          <p role="alert" className="text-sm text-state-danger-light dark:text-state-danger-dark">
            {userLoadError
              ? t('dataExport.errors.profileLoadFailed')
              : t('dataExport.errors.loadFailed')}
          </p>
          <Button variant="secondary" onClick={() => void fetchUser()} isLoading={userLoading}>
            {t('dataExport.retry')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {pageHeader}

        {/* Message banner */}
        {statusError && pollingExportId && (
          <div
            role="alert"
            className="rounded-xl bg-state-danger-bg-light p-4 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
          >
            <p>{t('dataExport.errors.statusFailed')}</p>
            <Button className="mt-3" onClick={() => checkExportStatus(pollingExportId)}>
              {t('common:actions.retry')}
            </Button>
          </div>
        )}
        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            aria-live={message.type === 'error' ? 'assertive' : 'polite'}
            className={`rounded-lg border p-4 text-sm ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Export Request Section */}
        <div className="rounded-xl border border-surface-border-light bg-surface-card-light p-4 shadow-soft dark:border-surface-border-dark dark:bg-surface-card-dark sm:p-6">
          <h2 className="mb-4 text-lg font-semibold leading-6 text-text-primary-light dark:text-text-primary-dark">
            {t('dataExport.requestTitle')}
          </h2>

          <div className="grid grid-cols-1 gap-6">
            {/* Format Selection */}
            <div>
              <fieldset>
                <legend className="mb-2 text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                  {t('dataExport.format')}
                </legend>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-surface-border-light px-3 text-sm text-text-primary-light dark:border-surface-border-dark dark:text-text-primary-dark">
                    <input
                      type="radio"
                      disabled={requesting}
                      name="format"
                      value="json"
                      checked={selectedFormat === 'json'}
                      onChange={(e) => setSelectedFormat(e.target.value as 'json' | 'csv')}
                      className="h-5 w-5 accent-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    />
                    <span>JSON</span>
                  </label>
                  <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-surface-border-light px-3 text-sm text-text-primary-light dark:border-surface-border-dark dark:text-text-primary-dark">
                    <input
                      type="radio"
                      disabled={requesting}
                      name="format"
                      value="csv"
                      checked={selectedFormat === 'csv'}
                      onChange={(e) => setSelectedFormat(e.target.value as 'json' | 'csv')}
                      className="h-5 w-5 accent-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    />
                    <span>CSV</span>
                  </label>
                </div>
              </fieldset>
            </div>

            {/* Export Details */}
            <div className="bg-state-info-bg-light dark:bg-state-info-bg-dark border-l-4 border-l-state-info-light dark:border-l-state-info-dark p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-state-info-light dark:text-state-info-dark"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-state-info-light dark:text-state-info-dark">
                    {t('dataExport.includedTitle')}
                  </h3>
                  <div className="mt-2 text-sm text-state-info-light dark:text-state-info-dark">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>{t('dataExport.included.profile')}</li>
                      <li>{t('dataExport.included.organization')}</li>
                      <li>{t('dataExport.included.backups')}</li>
                      <li>{t('dataExport.included.backupJobs')}</li>
                      <li>{t('dataExport.included.auditLogs')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Button */}
            <div className="flex justify-stretch sm:justify-end">
              <button
                onClick={handleRequestExport}
                disabled={requesting}
                className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-transparent bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {requesting ? t('dataExport.requesting') : t('dataExport.request')}
              </button>
            </div>
          </div>
        </div>

        {/* Export History */}
        <div className="overflow-hidden rounded-xl border border-surface-border-light bg-surface-card-light shadow-soft dark:border-surface-border-dark dark:bg-surface-card-dark">
          <div className="border-b border-surface-border-light px-4 py-4 dark:border-surface-border-dark sm:px-6">
            <h2 className="text-lg font-semibold leading-6 text-text-primary-light dark:text-text-primary-dark">
              {t('dataExport.historyTitle')}
            </h2>
          </div>

          {loading ? (
            <div className="px-6 py-4 text-center text-text-muted-light dark:text-text-muted-dark">
              {t('dataExport.loadingExports')}
            </div>
          ) : historyError ? (
            <div className="space-y-3 px-4 py-8 text-center" role="alert">
              <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
                {t('dataExport.errors.loadFailed')}
              </p>
              <button
                type="button"
                onClick={fetchExports}
                className="inline-flex min-h-11 items-center justify-center rounded-lg px-4 text-sm font-semibold text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:text-brand-300 dark:hover:bg-surface-2-dark"
              >
                {t('dataExport.retry')}
              </button>
            </div>
          ) : !exports || exports.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                {t('dataExport.emptyTitle')}
              </h3>
              <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
                {t('dataExport.emptyDescription')}
              </p>
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full divide-y divide-surface-border-light dark:divide-surface-border-dark">
                  <thead className="bg-surface-2-light dark:bg-surface-2-dark">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('dataExport.columns.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('dataExport.columns.created')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('dataExport.columns.expires')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('dataExport.columns.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface-card-light dark:bg-surface-card-dark divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {exports.map((exportItem) => (
                      <tr key={exportItem.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusChip
                            tone={getStatusTone(exportItem.status)}
                            label={getStatusLabel(exportItem.status)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                          {formatDate(exportItem.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted-light dark:text-text-muted-dark">
                          {formatDate(exportItem.expires_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {exportItem.status === 'COMPLETED' && exportItem.download_url ? (
                            <button
                              onClick={() => handleDownload(exportItem)}
                              disabled={!!downloadingId}
                              className="inline-flex min-h-11 items-center rounded-lg px-3 text-state-info-light hover:bg-state-info-bg-light dark:text-state-info-dark dark:hover:bg-state-info-bg-dark"
                            >
                              {t('dataExport.download')}
                            </button>
                          ) : (
                            <span
                              aria-label={t('dataExport.noActions')}
                              className="text-text-subtle-light dark:text-text-subtle-dark"
                            >
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-3 p-4 lg:hidden">
                {exports.map((exportItem) => (
                  <article
                    key={exportItem.id}
                    className="space-y-3 rounded-lg border border-surface-border-light p-4 dark:border-surface-border-dark"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <StatusChip
                        tone={getStatusTone(exportItem.status)}
                        label={getStatusLabel(exportItem.status)}
                      />
                      {exportItem.status === 'COMPLETED' && exportItem.download_url ? (
                        <button
                          disabled={!!downloadingId}
                          onClick={() => handleDownload(exportItem)}
                          className="inline-flex min-h-11 items-center rounded-lg px-3 text-sm font-semibold text-state-info-light hover:bg-state-info-bg-light dark:text-state-info-dark dark:hover:bg-state-info-bg-dark"
                        >
                          {t('dataExport.download')}
                        </button>
                      ) : null}
                    </div>
                    <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="text-text-muted-light dark:text-text-muted-dark">
                          {t('dataExport.columns.created')}
                        </dt>
                        <dd className="font-medium text-text-primary-light dark:text-text-primary-dark">
                          {formatDate(exportItem.created_at)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-text-muted-light dark:text-text-muted-dark">
                          {t('dataExport.columns.expires')}
                        </dt>
                        <dd className="font-medium text-text-primary-light dark:text-text-primary-dark">
                          {formatDate(exportItem.expires_at)}
                        </dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>

        {/* GDPR Notice */}
        <div className="mt-6 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-text-subtle-light dark:text-text-subtle-dark"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {t('dataExport.notice.title')}
              </h3>
              <div className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                <p className="mb-2">{t('dataExport.notice.description')}</p>
                <p className="mb-2">
                  <strong>{t('dataExport.notice.retention')}</strong>
                </p>
                <p>
                  {t('dataExport.notice.help')}{' '}
                  <a
                    href="mailto:support@pgkb.com"
                    className="text-state-info-light dark:text-state-info-dark hover:opacity-80"
                  >
                    support@pgkb.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
